import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import { styled } from '@mui/material/styles';
// utils
import { formatNumber } from 'utils/helpers/format';

const OriginName = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 'bold',
  lineHeight: '18px',
  margin: theme.spacing('2px', 0, 0.5),
}));

const SectionDiv = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  width: '100%',
}));

const anyOriginButtonStyles = {
  my: theme => theme.spacing(2), // margin-top & margin-bottom = '16px'
};

const SelectOrigins = ({ data, initialLoad, origins, setAllowContinue, setValue }) => {
  const { anyOrigin, originIds } = data;
  // want allOrigins to remember its value through rerenders, but not cause a rerender when calculated
  const allOrigins = useRef([]);
  useEffect(() => {
    // origins should be static, want to reset allOrigins.current when coming from page 3
    const mappedOriginIds = origins.map(origin => origin.originId);
    allOrigins.current = mappedOriginIds;
    // this will only run when origins has a length > 0;
    if (origins.length && !originIds.length && initialLoad) {
      setValue('originIds', mappedOriginIds);
      setValue('anyOrigin', true);
    }
    if (!initialLoad) {
      setAllowContinue(originIds.length > 0);
    }

    // we don't want to call this when originsId changes, since we are also setting its value here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origins, setValue]);

  const handleClickUseAnyOrigin = () => {
    setValue('originIds', allOrigins.current);
    setValue('anyOrigin', true);
    setAllowContinue(true);
  };

  const handleToggleOrigin = (originString, value) => {
    const originId = parseInt(originString, 10);
    let update = originIds.slice();
    if (value) {
      update.push(originId);
    } else {
      update = update.filter(origin => origin !== originId);
    }
    setValue('originIds', update);
    const allOriginsSelected = update.length === origins.length;
    setValue('anyOrigin', allOriginsSelected);
    setAllowContinue(update.length !== 0);
  };

  return (
    <SectionDiv display="flex" flexDirection="column">
      <Typography variant="h7">What Origins would you like to use?</Typography>
      <Button
        disabled={anyOrigin}
        onClick={handleClickUseAnyOrigin}
        sx={anyOriginButtonStyles}
        variant="contained"
      >
        Use Any Origin
      </Button>
      {origins.map(origin => (
        <Box key={origin.originId} mb={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={anyOrigin || originIds.includes(origin.originId)}
                checkedIcon={<CheckBoxRoundedIcon sx={{ color: 'secondary.main', m: 1 }} />} // change color to secondary.main (#22883A) currently not picking up the sx theming
                disabled={
                  (anyOrigin || originIds.includes(origin.originId)) && originIds.length === 0
                }
                icon={<CheckBoxOutlineBlankRoundedIcon sx={{ m: 1 }} />}
                name={`${origin.originId}`}
                onChange={(_, value) => {
                  handleToggleOrigin(origin.originId, value);
                }}
                sx={{
                  '&.MuiCheckbox-root': {
                    borderRadius: '50%',
                    color: 'text.secondary',
                  },
                }}
              />
            }
            label={
              <Box display="block">
                <OriginName display="block" variant="caption">
                  {origin.originNameLong}
                </OriginName>
                {!data.isFutureDate && (
                  <Box>
                    <Typography className="css-heap-redact" display="block" variant="caption">
                      {`${formatNumber(origin.availableInventoryBalance)} Available |
                    ${formatNumber(origin.intransitInventoryBalance)} In-Transit`}
                    </Typography>
                  </Box>
                )}
              </Box>
            }
          />
        </Box>
      ))}
    </SectionDiv>
  );
};

SelectOrigins.propTypes = {
  data: PropTypes.shape({
    anyOrigin: PropTypes.bool,
    isFutureDate: PropTypes.bool,
    originIds: PropTypes.arrayOf(PropTypes.number),
  }).isRequired,
  initialLoad: PropTypes.bool.isRequired,
  origins: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setAllowContinue: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
};

export default SelectOrigins;
